import {ChangeDetectionStrategy,afterNextRender,ChangeDetectorRef,Component,computed,DestroyRef,inject,linkedSignal,PLATFORM_ID,REQUEST,Signal,signal} from '@angular/core';
import {MATERIAL_IMPORTS} from '../shared/material-imports';
import {COMMON_IMPORTS} from '../shared/common-imports';
import {GALLERY_IMPORTS,GalleryMediaItem} from '../shared/gallery-imports';
import {DOCUMENT,isPlatformServer} from '@angular/common';
import {ActivatedRoute,Router,UrlTree} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {of,tap} from 'rxjs';
import {ComponentType} from '@angular/cdk/portal';
import {LoginPopupComponent} from '../authentication/login-popup/login-popup';
import {SignupPopupComponent} from '../authentication/signup-popup/signup-popup';
import {ForgotPasswordPopupComponent} from '../authentication/forgot-password-popup/forgot-password-popup';
import {environment} from '../../environments/environment';
import {rxResource,takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Meta,Title} from '@angular/platform-browser';
import {GetProductsQuery} from './graphql/get-products.generated';
import {IndexService} from './index.service';
import {GetProductsSortFactor} from '../common/graphql/types.generated';
import {ProductsSliderComponent} from '../misc/components/products-slider/products-slider';
import {VideoActiveDirective} from '../misc/directives/video-active/video-active.directive';
import {AppService} from '../app.service';
import {GetProductHistoryCartQuery} from '../common/graphql/get-product-history-cart.generated';
import {GetCartQuery} from '../common/graphql/get-cart.generated';
import {GetProductHistoryQuery} from '../common/graphql/get-product-history.generated';
import {UserResponseDto} from '../authentication/dto/user-response.dto';
import {AuthenticationService} from '../authentication/authentication.service';
import {LoginPhonePopupComponent} from '../authentication/login-phone-popup/login-phone-popup';
import {TotpVerifyComponent} from '../authentication/totp-verify/totp-verify';

@Component({
	selector:'app-index',
	changeDetection:ChangeDetectionStrategy.OnPush,
	imports:[
		...MATERIAL_IMPORTS,
		...COMMON_IMPORTS,
		...GALLERY_IMPORTS,
		ProductsSliderComponent,
		VideoActiveDirective
	],
	templateUrl:'./index.html',
	styleUrl:'./index.scss'
})
export class IndexComponent{
	protected readonly environment=environment;
	private activatedRoute=inject(ActivatedRoute);
	private matDialog=inject(MatDialog);
	private router=inject(Router);
	private readonly changeDetectorRef=inject(ChangeDetectorRef);
	private readonly destroyRef=inject(DestroyRef);
	pictures:GalleryMediaItem[];
	appService=inject(AppService);
	authenticationService=inject(AuthenticationService);
	private readonly indexService=inject(IndexService);
	itemsPerPageProducts=signal<number>(5);
	pageProducts=signal<number>(0);
	private productsResource=rxResource<GetProductsQuery['getProducts'],{itemsPerPage:number,page:number}>({
		id:'index-products',
		params:()=>({page:this.pageProducts(),itemsPerPage:this.itemsPerPageProducts()}),
		stream:({params})=>{
			const request=params;
			if(!request) return of([]);
			const page=params.page;
			const itemsPerPage=params.itemsPerPage;
			if(page==null || !itemsPerPage) return of([]);
			else return this.indexService.getProducts({
				limit:itemsPerPage,
				skip:page*itemsPerPage,
				sortFactor:GetProductsSortFactor.Recommended,
				sortOrder:1,
				filter:{
					discount:false,
					freeShipping:false
				}
			});
		}
	});
	products=linkedSignal<Signal<GetProductsQuery['getProducts']|undefined>,GetProductsQuery['getProducts']>({
		source:()=>this.productsResource.value,
		computation:(source,previous)=>{
			const products=source() || [];
			return previous?.value ? [...previous.value,...products] : products;
		}
	});
	productsError=computed<unknown>(()=>this.productsResource.error());
	productsLoading=computed<boolean>(()=>this.productsResource.isLoading());
	submitButtonActive=signal<boolean>(true);
	productsUrlTree:UrlTree=this.router.createUrlTree(['/products'],{queryParams:{sort:`${GetProductsSortFactor.Recommended}*1`}});
	itemsPerPageProductHistoryCart=signal<number>(5);
	pageProductHistoryCart=signal<number>(0);
	private productHistoryCartResource=rxResource<GetProductHistoryCartQuery['getProductHistoryCart'],{cart:GetCartQuery['getCart']|undefined,itemsPerPage:number,page:number}>({
		params:()=>({cart:this.appService.cart(),page:this.pageProductHistoryCart(),itemsPerPage:this.itemsPerPageProductHistoryCart()}),
		stream:({params})=>{
			const request=params;
			if(!request) return of([]);
			const cart=params.cart;
			const page=params.page;
			const itemsPerPage=params.itemsPerPage;
			if(!cart || page==null || !itemsPerPage) return of([]);
			else return this.appService.getProductHistoryCart({
				limit:itemsPerPage,
				skip:page*itemsPerPage,
				id:cart.id
			});
		}
	});
	productHistoryCart=linkedSignal<Signal<GetProductHistoryCartQuery['getProductHistoryCart']|undefined>,GetProductHistoryCartQuery['getProductHistoryCart']>({
		source:()=>this.productHistoryCartResource.value,
		computation:(source,previous)=>{
			const products=source() || [];
			return previous?.value ? [...previous.value,...products] : products;
		}
	});
	productHistoryCartError=computed<unknown>(()=>this.productHistoryCartResource.error());
	productHistoryCartLoading=computed<boolean>(()=>this.productHistoryCartResource.isLoading());
	itemsPerPageProductHistory=signal<number>(5);
	pageProductHistory=signal<number>(0);
	private productHistoryResource=rxResource<GetProductHistoryQuery['getProductHistory'],{user:UserResponseDto|null|undefined,itemsPerPage:number,page:number}>({
		params:()=>({user:this.authenticationService.user(),page:this.pageProductHistory(),itemsPerPage:this.itemsPerPageProductHistory()}),
		stream:({params})=>{
			const request=params;
			if(!request) return of([]);
			const user=params.user;
			const page=params.page;
			const itemsPerPage=params.itemsPerPage;
			if(!user || page==null || !itemsPerPage) return of([]);
			else return this.appService.getProductHistory({
				limit:itemsPerPage,
				skip:page*itemsPerPage
			});
		}
	});
	productHistory=linkedSignal<Signal<GetProductHistoryQuery['getProductHistory']|undefined>,GetProductHistoryQuery['getProductHistory']>({
		source:()=>this.productHistoryResource.value,
		computation:(source,previous)=>{
			const products=source() || [];
			return previous?.value ? [...previous.value,...products] : products;
		}
	});
	productHistoryError=computed<unknown>(()=>this.productHistoryResource.error());
	productHistoryLoading=computed<boolean>(()=>this.productHistoryResource.isLoading());
	
	
	private readonly title=inject(Title);
	private readonly meta=inject(Meta);
	private readonly documentRef=inject(DOCUMENT);
	private readonly platformId=inject(PLATFORM_ID);
	private readonly request=inject(REQUEST,{optional:true});

	constructor(){
		const {title,meta,platformId}=this;
		title.setTitle($localize`:@@index-title|Home page title:Bendita`);
		meta.updateTag({content:$localize`:@@index-og-title|Home og title:Bendita`},'property="og:title"');
		meta.updateTag({content:$localize`:@@seo-description-default|Default SEO description:Plantas & Semillas Abierto las 24 horas`},'name="description"');
		meta.updateTag({content:$localize`:@@seo-description-default|Default SEO description:Plantas & Semillas Abierto las 24 horas`},'property="og:description"');
		meta.updateTag({content:$localize`:@@seo-description-default|Default SEO description:Plantas & Semillas Abierto las 24 horas`},'property="og:image:alt"');
		meta.updateTag({content:$localize`:@@seo-keywords-default|Default SEO keywords:bendita, grow, shop, semillas, plantas`},'name="keywords"');
		meta.updateTag({content:`${environment.siteUrl}/icons/icon-512x512.png`},'property="og:image"');
		this.pictures=[
			{src:`${environment.siteUrl}/images/home1.png`,thumb:`${environment.siteUrl}/images/home1.png`,alt:'Bendita',type:'image',width:1681,height:929},
			{src:`${environment.siteUrl}/images/home2.png`,thumb:`${environment.siteUrl}/images/home2.png`,alt:'Bendita',type:'image',width:1259,height:927},
			{src:`${environment.siteUrl}/images/home3.png`,thumb:`${environment.siteUrl}/images/home3.png`,alt:'Bendita',type:'image',width:1259,height:927},
			{src:`${environment.siteUrl}/images/home4.png`,thumb:`${environment.siteUrl}/images/home4.png`,alt:'Bendita',type:'image',width:1904,height:851},
			{src:`${environment.siteUrl}/videos/home1.mp4`,poster:`${environment.siteUrl}/videos/home1.png`,thumb:`${environment.siteUrl}/videos/home1.png`,type:'video',width:1200,height:674}
		];
		
		afterNextRender(()=>{
			if(!isPlatformServer(platformId)){
				this.activatedRoute.url.pipe(
					tap((urlSegments)=>{
						let component:ComponentType<unknown>|undefined;
						let disableClose=false;
						switch(urlSegments[0]?.path){
							case('login'):{
								component=LoginPopupComponent;
								break;
							}
							case('signup'):{
								component=SignupPopupComponent;
								break;
							}
							case('forgot-password'):{
								component=ForgotPasswordPopupComponent;
								break;
							}
							case('login-phone'):{
								component=LoginPhonePopupComponent;
								break;
							}
							case('2fa'):{
								component=TotpVerifyComponent;
								disableClose=true;
								break;
							}
						}
						if(component){
							this.matDialog.open(component,{disableClose})
							.afterClosed()
							.subscribe((data)=>{
								if(data?.redirectUrl) void this.router.navigate([data.redirectUrl]);
								this.changeDetectorRef.detectChanges();
							});
						}
					}),
					takeUntilDestroyed(this.destroyRef)
				).subscribe();
			}
		});
	}
	
	fetchMoreProducts():void{
		this.pageProducts.set(this.pageProducts()+1);
	}
	
	fetchMoreProductHistoryCart():void{
		this.pageProductHistoryCart.set(this.pageProductHistoryCart()+1);
	}
	
	fetchMoreProductHistory():void{
		this.pageProductHistory.set(this.pageProductHistory()+1);
	}
	
}
